import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { mainApi } from "../../api/axios";

/**
 * Get order stats
 */
export const getOrderStats = createAsyncThunk(
  "report/getOrderStats",
  async ({ storeId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/report/sales/summary?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`,
      );
      return res.data[0] || {};
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Get Net Sales payment summary
 */

export const getNetSalesPaymentSummary = createAsyncThunk(
  "report/getNetSalesPaymentSummary",
  async ({ storeId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/report/sales/trend/store?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`,
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Get Sales summary
 */
export const getSalesSummary = createAsyncThunk(
  "report/getSalesSummary",
  async ({ storeId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/report/sales/summary/daily?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`,
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Get Sales payment summary
 */
export const getSalesPaymentSummary = createAsyncThunk(
  "report/getSalesPaymentSummary",
  async ({ storeId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/report/sales/payment-summary?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`,
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Get category sales summary
 */
export const getCategorySalesSummary = createAsyncThunk(
  "report/getCategorySalesSummary",
  async ({ storeId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/report/sales/summary/category?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`,
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const initialState = {
  orderStats: {},
  paymentSummary: [],
  netSalesSummary: [],
  salesSummary: [],
  categorySalesSummary: [],
  loading: false,
  error: null,
};

// Slice
const reportSlice = createSlice({
  name: "report",
  initialState: initialState,
  reducers: {
    clearReportState: (state) => {
      state.orderStats = {};
      state.paymentSummary = [];
      state.netSalesSummary = [];
      state.salesSummary = [];
      state.categorySalesSummary = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Order Stats
      .addCase(getOrderStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderStats.fulfilled, (state, action) => {
        state.loading = false;
        state.orderStats = action.payload;
      })
      .addCase(getOrderStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Net Sales Payment Summary
      .addCase(getNetSalesPaymentSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNetSalesPaymentSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.netSalesSummary = action.payload;
      })
      .addCase(getNetSalesPaymentSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Sales Summary
      .addCase(getSalesSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalesSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.salesSummary = action.payload;
      })
      .addCase(getSalesSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Sales Payment Summary
      .addCase(getSalesPaymentSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalesPaymentSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentSummary = action.payload;
      })
      .addCase(getSalesPaymentSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    // Get Category Sales Summary
    builder
      .addCase(getCategorySalesSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategorySalesSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.categorySalesSummary = action.payload;
      })
      .addCase(getCategorySalesSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearReportState } = reportSlice.actions;
export default reportSlice.reducer;
